import { SectionHeader } from '../common/section-header'
import { FilterTh } from '../common/column-filter-indicator'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import {
  ClearFiltersButton,
  FilterField,
  ListPage,
  ListPagination,
  SearchField,
} from '../common/list-page'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW, TABLE_WRAP } from '../common/table'
import { Badge, type BadgeProps, Button, Icon, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { adminJobsApi, messageForJobsError } from './api'
import { SortableTh, type SortDir } from '../common/sortable-th'
import { describeJobSchedule } from './schedule'
import type {
  JobAttempt,
  JobAttemptSort,
  JobDefinition,
  JobStatus,
  ListJobAttemptsParams,
  ListJobDefinitionsParams,
} from './types'

const PAGE_SIZE = 50
const REFRESH_MS = 60_000
const RELEASE_DEBOUNCE_MS = 250
const CATALOG_PARAMS: ListJobDefinitionsParams = {}
const WINDOWS = ['1h', '24h', '7d', '30d', 'all'] as const
type WindowValue = (typeof WINDOWS)[number]
type AttemptSortKey = 'queued' | 'queueWait' | 'duration'
const ATTEMPT_SORTS: readonly JobAttemptSort[] = [
  'queued_desc',
  'queued_asc',
  'queue_wait_desc',
  'queue_wait_asc',
  'duration_desc',
  'duration_asc',
]

function attemptSort(value: string | null): JobAttemptSort {
  return ATTEMPT_SORTS.includes(value as JobAttemptSort)
    ? (value as JobAttemptSort)
    : 'queued_desc'
}

function attemptSortKey(value: JobAttemptSort): AttemptSortKey {
  if (value.startsWith('queue_wait')) return 'queueWait'
  if (value.startsWith('duration')) return 'duration'
  return 'queued'
}

function attemptSortDirection(value: JobAttemptSort): SortDir {
  return value.endsWith('_asc') ? 'asc' : 'desc'
}

function attemptSortValue(key: AttemptSortKey, direction: SortDir): JobAttemptSort {
  const field = key === 'queueWait' ? 'queue_wait' : key
  return `${field}_${direction}` as JobAttemptSort
}

const STATUSES: readonly JobStatus[] = [
  'queued',
  'running',
  'retrying',
  'partial',
  'failed',
  'timed_out',
  'aborted',
  'abandoned',
  'succeeded',
]

function parseStatus(value: string | null): JobStatus | '' {
  return STATUSES.find((status) => status === value) ?? ''
}

function parseWindow(value: string | null): WindowValue {
  return WINDOWS.find((window) => window === value) ?? '24h'
}

function sinceFor(window: WindowValue, anchor: number): string | undefined {
  const hours = window === '1h' ? 1 : window === '24h' ? 24 : window === '7d' ? 168 : 720
  return window === 'all' ? undefined : new Date(anchor - hours * 3_600_000).toISOString()
}

function formatWhen(value: string | null): string {
  if (value === null) return '—'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value))
}

function formatDuration(value: number | null): string {
  if (value === null) return '—'
  if (value < 1_000) return `${value} ms`
  if (value < 60_000) return `${(value / 1_000).toFixed(1)} s`
  return `${(value / 60_000).toFixed(1)} min`
}

function relativeAge(value: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1_000))
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h ago`
  return `${Math.floor(seconds / 86_400)}d ago`
}

function statusVariant(status: JobStatus): BadgeProps['variant'] {
  if (['failed', 'timed_out', 'aborted', 'abandoned', 'partial'].includes(status)) {
    return 'danger'
  }
  if (['queued', 'running', 'retrying'].includes(status)) return 'muted'
  return 'neutral'
}

function statusLabel(status: string): string {
  return status.replaceAll('_', ' ')
}

function latestVerdict(definition: JobDefinition): {
  label: string
  attention: boolean
  timestamp: string | null
} {
  if (!definition.enabled) return { label: 'Disabled', attention: false, timestamp: null }
  const attempt = definition.latestAttempt
  if (attempt === null) {
    return { label: 'Never observed', attention: definition.kind === 'cron', timestamp: null }
  }
  const timestamp = attempt.startedAt ?? attempt.queuedAt
  const overdue =
    definition.kind === 'cron' &&
    definition.missedAfterSeconds !== null &&
    Date.now() - new Date(timestamp).getTime() > definition.missedAfterSeconds * 1_000
  if (overdue) return { label: 'Expected run missing', attention: true, timestamp }
  return {
    label: statusLabel(attempt.status),
    attention: statusVariant(attempt.status) === 'danger',
    timestamp,
  }
}

function HeartbeatStrip({ definitions }: { definitions: JobDefinition[] }): JSX.Element {
  return (
    <details open aria-labelledby="job-heartbeats" className="mb-5">
      <summary className="mb-2 cursor-pointer text-text-primary">
        <span id="job-heartbeats" role="heading" aria-level={2} className="font-display text-section-title font-semibold">
          Latest heartbeat
        </span>
        <span className="ml-3 text-caption text-text-secondary">Refreshes every minute</span>
      </summary>
      {definitions.length === 0 ? (
        <div className="rounded-token-card bg-surface-card p-4 text-body text-text-secondary">
          No heartbeats match the current investigation filters.
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {definitions.map((definition) => {
          const verdict = latestVerdict(definition)
          return (
            <article
              key={definition.jobName}
              className={
                'rounded-token-card border-l-4 bg-surface-card p-3 shadow-raised ' +
                (verdict.attention ? 'border-l-danger' : 'border-l-accent')
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate font-mono text-body font-medium" title={definition.jobName}>
                    {definition.jobName}
                  </h3>
                  <p
                    className="mt-0.5 text-caption text-text-secondary"
                    title={
                      definition.schedule === null
                        ? undefined
                        : `${definition.schedule} (${definition.timezone ?? 'worker timezone'})`
                    }
                  >
                    {describeJobSchedule(definition.schedule, definition.timezone)}
                  </p>
                  {definition.scheduleRationale !== null && (
                    <details className="mt-1 text-caption text-text-secondary">
                      <summary className="cursor-pointer select-none font-medium text-text-primary">
                        Why this schedule?
                      </summary>
                      <p className="mt-1 leading-relaxed">{definition.scheduleRationale}</p>
                    </details>
                  )}
                </div>
                <Badge variant={verdict.attention ? 'danger' : 'neutral'}>
                  {verdict.label}
                </Badge>
              </div>
              <p className="mt-3 text-caption text-text-secondary">
                {verdict.timestamp === null ? 'No attempt timestamp' : relativeAge(verdict.timestamp)}
                {' · '}
                {definition.owner}
              </p>
            </article>
          )
        })}
        </div>
      )}
    </details>
  )
}

export function JobRunsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [releaseInput, setReleaseInput] = useState(searchParams.get('release') ?? '')
  const [release, setRelease] = useState(releaseInput.trim())
  const jobName = searchParams.get('job') ?? ''
  const status = parseStatus(searchParams.get('status'))
  const windowValue = parseWindow(searchParams.get('window'))

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = releaseInput.trim()
      setRelease(next)
      setPage(0)
      setSearchParams((current) => {
        const params = new URLSearchParams(current)
        if (next) params.set('release', next)
        else params.delete('release')
        return params
      })
    }, RELEASE_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [releaseInput, setSearchParams])

  const heartbeatParams: ListJobDefinitionsParams = useMemo(
    () => ({
      jobName: jobName || undefined,
      status: status || undefined,
      release: release || undefined,
    }),
    [jobName, release, status],
  )
  const sort = attemptSort(searchParams.get('sort'))
  const activeSortKey = attemptSortKey(sort)
  const sortDirection = attemptSortDirection(sort)
  const params: ListJobAttemptsParams = useMemo(
    () => ({
      ...heartbeatParams,
      sort,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [heartbeatParams, page, sort],
  )
  const catalogQuery = useQuery({
    queryKey: queryKeys.jobDefinitions(CATALOG_PARAMS),
    queryFn: () => adminJobsApi.definitions(CATALOG_PARAMS),
    refetchInterval: REFRESH_MS,
  })
  const definitionsQuery = useQuery({
    queryKey: [...queryKeys.jobDefinitions(heartbeatParams), windowValue],
    queryFn: () =>
      adminJobsApi.definitions({
        ...heartbeatParams,
        since: sinceFor(windowValue, Date.now()),
      }),
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
  })
  const catalogDefinitions = catalogQuery.data?.rows ?? []
  const definitions = definitionsQuery.data?.rows ?? []
  const attemptsQuery = useQuery({
    queryKey: [...queryKeys.jobAttempts(params), windowValue],
    queryFn: () =>
      adminJobsApi.attempts({
        ...params,
        since: sinceFor(windowValue, Date.now()),
      }),
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
  })
  const attempts = attemptsQuery.data?.rows ?? []
  const [expanded, setExpanded] = useState<string | null>(null)

  const updateFilter = (key: string, value: string): void => {
    setPage(0)
    setSearchParams((current) => {
      const params = new URLSearchParams(current)
      if (value) params.set(key, value)
      else params.delete(key)
      return params
    })
  }
  const toggleSort = (key: AttemptSortKey): void => {
    const direction: SortDir =
      key === activeSortKey
        ? sortDirection === 'desc'
          ? 'asc'
          : 'desc'
        : 'desc'
    const next = attemptSortValue(key, direction)
    updateFilter('sort', next === 'queued_desc' ? '' : next)
  }
  const clearFilters = (): void => {
    setReleaseInput('')
    setRelease('')
    setPage(0)
    setSearchParams(new URLSearchParams())
  }
  const activeFilterCount =
    Number(Boolean(jobName)) +
    Number(Boolean(status)) +
    Number(Boolean(release)) +
    Number(windowValue !== '24h')
  const error = catalogQuery.error ?? definitionsQuery.error ?? attemptsQuery.error

  return (
    <ListPage
      title="Job runs"
      action={
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            void catalogQuery.refetch()
            void definitionsQuery.refetch()
            void attemptsQuery.refetch()
          }}
          disabled={catalogQuery.isFetching || definitionsQuery.isFetching || attemptsQuery.isFetching}
        >
          <Icon name="refresh" />
          Refresh
        </Button>
      }
      search={
        <SearchField
          id="job-release"
          label="Filter by exact release"
          placeholder="Filter by exact release, for example 2026.08.20.1"
          value={releaseInput}
          onChange={(event) => setReleaseInput(event.target.value)}
        />
      }
      filterAction={
        <ClearFiltersButton
          hasFilters={activeFilterCount > 0 || releaseInput !== ''}
          onClear={clearFilters}
        />
      }
      filters={
        <>
          <FilterField active={jobName !== ''} label="Job" htmlFor="job-name-filter">
            <Select value={jobName} onValueChange={(value) => updateFilter('job', value)}>
              <SelectTrigger id="job-name-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All jobs</SelectItem>
                {catalogDefinitions.map((definition) => (
                  <SelectItem key={definition.jobName} value={definition.jobName}>
                    {definition.jobName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={status !== ''} label="Outcome" htmlFor="job-status-filter">
            <Select value={status} onValueChange={(value) => updateFilter('status', value)}>
              <SelectTrigger id="job-status-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Every outcome</SelectItem>
                {STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {statusLabel(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField active={windowValue !== 'all'} label="Window" htmlFor="job-window-filter">
            <Select value={windowValue} onValueChange={(value) => updateFilter('window', value)}>
              <SelectTrigger id="job-window-filter" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Last hour</SelectItem>
                <SelectItem value="24h">Last 24 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="all">All exact history</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </>
      }
      activeFilterCount={activeFilterCount}
      pagination={
        attemptsQuery.isSuccess && attempts.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing attempts {page * PAGE_SIZE + 1}–
                {page * PAGE_SIZE + attempts.length}
              </>
            }
            previousDisabled={page === 0}
            nextDisabled={!attemptsQuery.data?.hasMore}
            onPrevious={() => setPage((value) => value - 1)}
            onNext={() => setPage((value) => value + 1)}
          />
        ) : undefined
      }
    >
      {error !== null && (
        <div role="alert" className="mb-4 rounded-token-card border border-danger/40 bg-danger/10 p-3 text-body text-danger-text">
          {messageForJobsError(error)}
        </div>
      )}
      <HeartbeatStrip definitions={definitions} />
      <section aria-labelledby="attempt-history">
        <SectionHeader
          id="attempt-history"
          title="Exact attempt history"
          meta={<span className="text-caption text-text-secondary">
            Raw attempts retained {definitions[0]?.exactRetentionDays ?? 90} days
          </span>}
          className="mb-2"
        />
        {attemptsQuery.isPending ? (
          <p className="text-body text-text-secondary">Loading job attempts…</p>
        ) : attempts.length === 0 ? (
          <div className="rounded-token-card bg-surface-card p-6 text-body text-text-secondary">
            No attempts match this investigation window.
          </div>
        ) : (
          <>
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
                <thead>
                  <tr className={TABLE_HEAD_ROW}>
                    <FilterTh className="p-3" label="Outcome" filterDescription={status ? `Outcome: ${status.replaceAll('_', ' ')}` : undefined} />
                    <FilterTh className="p-3" label="Job" filterDescription={jobName ? `Job: ${jobName}` : undefined} />
                    <SortableTh
                      label="Queued"
                    filterDescription={windowValue !== 'all' ? `Queued within ${windowValue}` : undefined}
                      columnKey="queued"
                      activeKey={activeSortKey}
                      dir={sortDirection}
                      onSort={toggleSort}
                    />
                    <SortableTh
                      label="Queue wait"
                      columnKey="queueWait"
                      activeKey={activeSortKey}
                      dir={sortDirection}
                      onSort={toggleSort}
                    />
                    <SortableTh
                      label="Processing"
                      columnKey="duration"
                      activeKey={activeSortKey}
                      dir={sortDirection}
                      onSort={toggleSort}
                    />
                    <th className="p-3">Attempt</th>
                    <FilterTh className="p-3" label="Release" filterDescription={release ? `Release: ${release}` : undefined} />
                    <th className="p-3"><span className="sr-only">Details</span></th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((attempt) => (
                    <AttemptRows
                      key={attempt.attemptId}
                      attempt={attempt}
                      expanded={expanded === attempt.attemptId}
                      onToggle={() => setExpanded(expanded === attempt.attemptId ? null : attempt.attemptId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </ListPage>
  )
}

function AttemptRows({
  attempt,
  expanded,
  onToggle,
}: {
  attempt: JobAttempt
  expanded: boolean
  onToggle: () => void
}): JSX.Element {
  return (
    <Fragment>
      <tr className={TABLE_ROW}>
        <td className="p-3"><Badge variant={statusVariant(attempt.status)}>{statusLabel(attempt.status)}</Badge></td>
        <td className="p-3 font-mono text-table-secondary">{attempt.jobName}</td>
        <td className="p-3 whitespace-nowrap text-text-secondary">{formatWhen(attempt.queuedAt)}</td>
        <td className="p-3 tabular-nums text-text-secondary">{formatDuration(attempt.queueWaitMs)}</td>
        <td className="p-3 tabular-nums text-text-secondary">{formatDuration(attempt.durationMs)}</td>
        <td className="p-3 tabular-nums text-text-secondary">{attempt.attempt}/{attempt.maxAttempts}</td>
        <td className="p-3 font-mono text-table-secondary text-text-secondary">{attempt.release}</td>
        <td className="p-3 text-right">
          <Button type="button" variant="ghost" size="sm" aria-expanded={expanded} onClick={onToggle}>
            {expanded ? 'Hide' : 'Inspect'}
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border-default bg-surface-card-inset">
          <td colSpan={8} className="p-4">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-table sm:grid-cols-3">
                <div><dt className="text-table-secondary text-text-secondary">Logical run</dt><dd className="break-all font-mono text-table-secondary">{attempt.logicalRunId}</dd></div>
                <div><dt className="text-table-secondary text-text-secondary">Started</dt><dd>{formatWhen(attempt.startedAt)}</dd></div>
                <div><dt className="text-table-secondary text-text-secondary">Finished</dt><dd>{formatWhen(attempt.finishedAt)}</dd></div>
                <div><dt className="text-table-secondary text-text-secondary">Environment</dt><dd>{attempt.environment}</dd></div>
                <div><dt className="text-table-secondary text-text-secondary">Worker</dt><dd className="break-all font-mono text-table-secondary">{attempt.workerId ?? '—'}</dd></div>
                <div><dt className="text-table-secondary text-text-secondary">Error category</dt><dd>{attempt.errorCategory ?? '—'}</dd></div>
              </dl>
              <div>
                <SectionHeader level={3} title={<>Bounded summary v{attempt.summaryVersion}</>} className="mb-1" />
                <pre className="max-h-52 overflow-auto rounded-token-card bg-surface-app p-3 text-table-secondary">{JSON.stringify(attempt.summary, null, 2)}</pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}
